import Phaser from 'phaser';
import { ColyseusClient, Player, Platform, GameState, TargetZone } from '../network/ColyseusClient';

interface PlayerData {
  sprite: Phaser.GameObjects.Rectangle;
  lastX: number;
  lastY: number;
  lastUpdateTime: number;
  velocityX: number;
  velocityY: number;
}

export class GameScene extends Phaser.Scene {
  private colyseusClient!: ColyseusClient;
  private localSessionId: string | null = null;
  private players: Map<string, Phaser.GameObjects.Rectangle> = new Map();
  private playerData: Map<string, PlayerData> = new Map();
  private platforms: Phaser.GameObjects.Rectangle[] = [];
  private targetZones: Phaser.GameObjects.Rectangle[] = [];
  private cursors!: Phaser.Types.Input.Keyboard.CursorKeys;
  private wordText!: Phaser.GameObjects.Text;
  private levelText!: Phaser.GameObjects.Text;
  private statusText!: Phaser.GameObjects.Text;
  private timerText!: Phaser.GameObjects.Text;
  private readyText!: Phaser.GameObjects.Text;
  private winText: Phaser.GameObjects.Text | null = null;
  private gameOverText: Phaser.GameObjects.Text | null = null;
  private lastWord: string = "";
  private lastLevel: number = 0;
  private lastSpacePressed: boolean = false;
  private wasdKeys!: {
    left: Phaser.Input.Keyboard.Key;
    right: Phaser.Input.Keyboard.Key;
    up: Phaser.Input.Keyboard.Key;
  };
  private spaceKey!: Phaser.Input.Keyboard.Key;
  private lastInputState = {
    left: false,
    right: false,
    jump: false
  };
  private lastUpdateTime: number = 0;

  constructor() {
    super({ key: 'GameScene' });
  }

  create() {
    // Initialize Colyseus client
    this.colyseusClient = new ColyseusClient();
    this.lastUpdateTime = Date.now();
    
    // Set up input
    this.cursors = this.input.keyboard!.createCursorKeys();
    const keyboard = this.input.keyboard!;
    this.wasdKeys = {
      left: keyboard.addKey('A'),
      right: keyboard.addKey('D'),
      up: keyboard.addKey('W')
    };
    this.spaceKey = keyboard.addKey('SPACE');

    // Create UI elements
    this.createUI();

    // Connect to server
    this.colyseusClient.connect('platformer').then(() => {
      this.localSessionId = this.colyseusClient.getSessionId();
      
      // Set up state change listener
      this.colyseusClient.onStateChange((state) => {
        this.updateGameState(state);
      });

      // Set up player join/leave listeners
      this.colyseusClient.onPlayerJoin((player) => {
        this.createPlayerSprite(player);
      });

      this.colyseusClient.onPlayerLeave((sessionId) => {
        this.removePlayerSprite(sessionId);
      });

      // Initial state sync
      const room = this.colyseusClient.getRoom();
      if (room && room.state) {
        this.updateGameState(room.state);
      }
    }).catch((error) => {
      console.error('Failed to connect to server:', error);
      this.add.text(400, 300, 'Failed to connect to server', {
        fontSize: '24px',
        color: '#ff0000'
      }).setOrigin(0.5);
    });
  }

  update(time: number, delta: number) {
    if (!this.colyseusClient || !this.localSessionId) {
      return;
    }

    const currentTime = Date.now();
    const deltaTime = (currentTime - this.lastUpdateTime) / 1000; // Convert to seconds
    this.lastUpdateTime = currentTime;

    // Smooth interpolation for all players using velocity-based prediction
    this.playerData.forEach((data, sessionId) => {
      const isLocal = sessionId === this.localSessionId;
      const sprite = data.sprite;
      
      // Use velocity to predict position for smoother movement
      const predictedX = sprite.x + data.velocityX * deltaTime;
      const predictedY = sprite.y + data.velocityY * deltaTime;
      
      // Interpolate towards target position with higher factor for smoother movement
      const interpolationFactor = isLocal ? 0.8 : 0.5; // Faster interpolation for local player
      sprite.x = Phaser.Math.Linear(predictedX, data.lastX, interpolationFactor);
      sprite.y = Phaser.Math.Linear(predictedY, data.lastY, interpolationFactor);

      // Update label position if it exists (letter display)
      const label = sprite.getData('label') as Phaser.GameObjects.Text | undefined;
      if (label) {
        label.x = sprite.x;
        label.y = sprite.y - 40;
      }
    });

    // Get current game state to check if we should handle ready toggle
    const room = this.colyseusClient.getRoom();
    const gameWon = room?.state?.gameWon || false;
    
    // Handle input
    const leftPressed = this.cursors.left.isDown || this.wasdKeys.left.isDown;
    const rightPressed = this.cursors.right.isDown || this.wasdKeys.right.isDown;
    const jumpPressed = this.cursors.up.isDown || this.wasdKeys.up.isDown;
    const spacePressed = this.spaceKey.isDown;

    // Handle ready toggle (SPACE when game is won)
    if (gameWon && spacePressed && !this.lastSpacePressed) {
      this.colyseusClient.sendToggleReady();
      this.lastSpacePressed = true;
    } else if (!spacePressed) {
      this.lastSpacePressed = false;
    }

    // Only handle movement/jump if game is not won
    if (!gameWon) {
      // Send input changes to server
      if (leftPressed && !this.lastInputState.left) {
        this.colyseusClient.sendInput('move-left');
        this.lastInputState.left = true;
        this.lastInputState.right = false;
      } else if (!leftPressed && this.lastInputState.left) {
        if (rightPressed) {
          this.colyseusClient.sendInput('move-right');
          this.lastInputState.right = true;
        } else {
          this.colyseusClient.sendInput('stop');
        }
        this.lastInputState.left = false;
      }

      if (rightPressed && !this.lastInputState.right) {
        this.colyseusClient.sendInput('move-right');
        this.lastInputState.right = true;
        this.lastInputState.left = false;
      } else if (!rightPressed && this.lastInputState.right) {
        if (leftPressed) {
          this.colyseusClient.sendInput('move-left');
          this.lastInputState.left = true;
        } else {
          this.colyseusClient.sendInput('stop');
        }
        this.lastInputState.right = false;
      }

      if (jumpPressed && !this.lastInputState.jump) {
        this.colyseusClient.sendInput('jump');
        this.lastInputState.jump = true;
      } else if (!jumpPressed) {
        this.lastInputState.jump = false;
      }
    }
  }

  private updateGameState(state: GameState) {
    // Update platforms (only create once)
    if (this.platforms.length === 0 && state.platforms) {
      state.platforms.forEach((platform) => {
        this.createPlatform(platform);
      });
    }

    // Update target zones (only create once)
    if (this.targetZones.length === 0 && state.targetZones) {
      state.targetZones.forEach((zone) => {
        this.createTargetZone(zone);
      });
    }

    // Update UI
    this.updateUI(state);

    // Update players
    if (state.players) {
      const currentPlayerIds = new Set<string>();
      
      // Update or create player sprites
      state.players.forEach((player: Player, sessionId: string) => {
        currentPlayerIds.add(sessionId);
        if (!this.players.has(sessionId)) {
          this.createPlayerSprite(player);
        } else {
          this.updatePlayerSprite(sessionId, player);
        }
      });

      // Remove players that no longer exist
      this.players.forEach((sprite, sessionId) => {
        if (!currentPlayerIds.has(sessionId)) {
          this.removePlayerSprite(sessionId);
        }
      });
    }
  }

  private createPlatform(platform: Platform) {
    const rect = this.add.rectangle(
      platform.x + platform.width / 2,
      platform.y + platform.height / 2,
      platform.width,
      platform.height,
      0x8b4513
    );
    rect.setStrokeStyle(2, 0x654321);
    this.platforms.push(rect);
  }

  private createPlayerSprite(player: Player) {
    const isLocal = player.sessionId === this.localSessionId;
    const color = isLocal ? 0x00ff00 : 0xff0000; // Green for local, red for remote
    
    const rect = this.add.rectangle(
      player.x,
      player.y,
      32,
      48,
      color
    );
    rect.setStrokeStyle(2, 0x000000);
    rect.setAlpha(0.8);
    
    // Add letter label for all players
    const letterText = player.letter || '?';
    const label = this.add.text(player.x, player.y - 40, letterText, {
      fontSize: '24px',
      color: '#ffffff',
      backgroundColor: '#000000',
      padding: { x: 8, y: 4 },
      fontStyle: 'bold'
    });
    label.setOrigin(0.5);
    rect.setData('label', label);

    // Store player data for smooth interpolation
    const playerData: PlayerData = {
      sprite: rect,
      lastX: player.x,
      lastY: player.y,
      lastUpdateTime: Date.now(),
      velocityX: player.velocityX,
      velocityY: player.velocityY
    };

    this.players.set(player.sessionId, rect);
    this.playerData.set(player.sessionId, playerData);
    this.updatePlayerSprite(player.sessionId, player);
  }

  private updatePlayerSprite(sessionId: string, player: Player) {
    const playerData = this.playerData.get(sessionId);
    if (playerData) {
      // Update target position and velocity for smooth interpolation
      playerData.lastX = player.x;
      playerData.lastY = player.y;
      playerData.velocityX = player.velocityX;
      playerData.velocityY = player.velocityY;
      playerData.lastUpdateTime = Date.now();

      const sprite = playerData.sprite;

      // Flip sprite based on facing direction
      if (player.facing === 'left') {
        sprite.setScale(-1, 1);
      } else {
        sprite.setScale(1, 1);
      }

      // Update letter label
      const label = sprite.getData('label') as Phaser.GameObjects.Text | undefined;
      if (label) {
        const letterText = player.letter || '?';
        label.setText(letterText);
      }
    }
  }

  private removePlayerSprite(sessionId: string) {
    const sprite = this.players.get(sessionId);
    if (sprite) {
      const label = sprite.getData('label') as Phaser.GameObjects.Text | undefined;
      if (label) {
        label.destroy();
      }
      sprite.destroy();
      this.players.delete(sessionId);
      this.playerData.delete(sessionId);
    }
  }

  private createUI() {
    // Word display - hidden
    this.wordText = this.add.text(400, 50, '', {
      fontSize: '32px',
      color: '#ffffff',
      backgroundColor: '#000000',
      padding: { x: 20, y: 10 },
      fontStyle: 'bold'
    });
    this.wordText.setOrigin(0.5);
    this.wordText.setVisible(false); // Hide the word

    // Level display
    this.levelText = this.add.text(400, 30, 'Level: 0', {
      fontSize: '20px',
      color: '#ffffff',
      backgroundColor: '#000000',
      padding: { x: 10, y: 5 }
    });
    this.levelText.setOrigin(0.5);

    // Timer display
    this.timerText = this.add.text(400, 60, 'Time: 20', {
      fontSize: '24px',
      color: '#ffff00',
      backgroundColor: '#000000',
      padding: { x: 10, y: 5 },
      fontStyle: 'bold'
    });
    this.timerText.setOrigin(0.5);

    // Status display
    this.statusText = this.add.text(400, 90, 'Players: 0/4', {
      fontSize: '16px',
      color: '#ffffff',
      backgroundColor: '#000000',
      padding: { x: 10, y: 5 }
    });
    this.statusText.setOrigin(0.5);

    // Ready status display
    this.readyText = this.add.text(400, 120, '', {
      fontSize: '18px',
      color: '#00ff00',
      backgroundColor: '#000000',
      padding: { x: 10, y: 5 }
    });
    this.readyText.setOrigin(0.5);
    this.readyText.setVisible(false);
  }

  private updateUI(state: GameState) {
    // Word display is hidden - just track for win detection
    if (state.currentWord !== this.lastWord) {
      this.lastWord = state.currentWord;
    }

    // Update level display
    if (state.level !== this.lastLevel) {
      this.levelText.setText(`Level: ${state.level}`);
      this.lastLevel = state.level;
    }

    // Update timer display
    const timeRemaining = Math.ceil(state.timeRemaining || 0);
    this.timerText.setText(`Time: ${timeRemaining}`);
    if (timeRemaining <= 5) {
      this.timerText.setStyle({ color: '#ff0000' });
    } else if (timeRemaining <= 10) {
      this.timerText.setStyle({ color: '#ff8800' });
    } else {
      this.timerText.setStyle({ color: '#ffff00' });
    }

    // Update status
    const playerCount = state.players ? state.players.size : 0;
    if (state.gameStarted) {
      this.statusText.setText(`Game Started! Players: ${playerCount}/4`);
      this.statusText.setStyle({ color: '#00ff00' });
    } else {
      this.statusText.setText(`Players: ${playerCount}/4`);
      this.statusText.setStyle({ color: '#ffffff' });
    }

    // Handle win screen
    if (state.gameWon) {
      this.showWinScreen(state.currentWord, state.readyPlayers);
    } else {
      this.hideWinScreen();
    }

    // Handle game over
    if (state.gameOver) {
      this.showGameOver();
    } else {
      this.hideGameOver();
    }
  }

  private showWinScreen(word: string, readyCount: number) {
    // Create or update win text
    if (!this.winText) {
      this.winText = this.add.text(400, 250, '', {
        fontSize: '48px',
        color: '#00ff00',
        backgroundColor: '#000000',
        padding: { x: 30, y: 15 },
        fontStyle: 'bold'
      });
      this.winText.setOrigin(0.5);
    }
    this.winText.setText(`WORD COMPLETED!\nThe word was: ${word}`);
    this.winText.setVisible(true);
    this.winText.setAlpha(1);

    // Show ready status
    this.readyText.setVisible(true);
    this.readyText.setText(`Ready: ${readyCount}/4\nPress SPACE to ready up!`);
    this.readyText.setPosition(400, 350);
  }

  private hideWinScreen() {
    if (this.winText) {
      this.winText.setVisible(false);
    }
    this.readyText.setVisible(false);
  }

  private showGameOver() {
    if (!this.gameOverText) {
      this.gameOverText = this.add.text(400, 300, 'TIME\'S UP!\nGAME OVER', {
        fontSize: '48px',
        color: '#ff0000',
        backgroundColor: '#000000',
        padding: { x: 30, y: 15 },
        fontStyle: 'bold'
      });
      this.gameOverText.setOrigin(0.5);
    }
    this.gameOverText.setVisible(true);
  }

  private hideGameOver() {
    if (this.gameOverText) {
      this.gameOverText.setVisible(false);
    }
  }

  private createTargetZone(zone: TargetZone) {
    const rect = this.add.rectangle(
      zone.x + zone.width / 2,
      zone.y + zone.height / 2,
      zone.width,
      zone.height,
      0x00ff00,
      0.2 // Semi-transparent
    );
    rect.setStrokeStyle(2, 0x00ff00);
    rect.setAlpha(0.3);
    this.targetZones.push(rect);

    // Add zone label showing expected letter position
    const label = this.add.text(
      zone.x + zone.width / 2,
      zone.y + zone.height / 2,
      `Pos ${zone.index + 1}`,
      {
        fontSize: '12px',
        color: '#00ff00',
        backgroundColor: '#000000',
        padding: { x: 4, y: 2 }
      }
    );
    label.setOrigin(0.5);
    rect.setData('label', label);
  }
}

